/* eslint-disable max-lines-per-function */
import { faker } from '@faker-js/faker';
import { generateInitialPaginationApi } from '@ingenico/api-sdk-core/mocks';
import type {
  DeviceCallResourceReadV2Api,
  DeviceResourceDiagnosticsListResponseApi,
  DeviceResourceDiagnosticsReadApi,
  DeviceResourceMetricsReadV2Api,
  DeviceResourceReadV2Api,
  DeviceResourceSoftwareReadV2Api,
  GetDeviceCallByIdV2QueryResponseApi,
  GetDeviceDiagnosticByDeviceIdAndDiagnosticIdV2QueryResponseApi,
  GetDeviceMetricsByDeviceIdV2QueryResponseApi,
  GetDeviceSoftwaresByDeviceIdV2QueryResponseApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  enumCallReason,
  enumDeviceConnectionStatusV2,
  enumDeviceDiagnosticsType,
  enumDeviceLogCapabilityStatusV2,
  enumDiagnosticTestExecutionStatus,
  enumDiagnosticTestModule,
  enumRemoteDesktopStatusV2,
} from '@ingenico/api-sdk-device-management-private-v2';
import type { GetDeviceByIdV2QueryResponseApi } from '@ingenico/api-sdk-device-management-public-v2';
import { HttpResponse } from 'msw';

const LOGS_AS_STRING =
  "12:12:12.787  ERROR Start handling OpenPOS call. Correlation id = d6735f19-92a0-420e-8001-449bfbca8333\n12:12:12.848  INFO Handshake from an OpenPOS terminal of type AXIUMNX.\n12:12:12.848  INFO Device name: 2025134, S/N: 225SCU805488\n12:12:12.848  WARNING Calling application: com.ingenico.dxmobile Activities: DOWNLOAD,DIAGNOSTIC,PARAMETER,REMOTE DESKTOP\n12:12:12.848  INFO Call reason: RTS_TRIGGERED\n12:12:12.852  INFO Attachment validated.\n12:12:12.852  INFO Handling maintenance call.\n12:12:12.852  INFO Read configuration\n12:12:14.522  INFO Call triggered by RTS. Retrieve token.\n12:12:14.553  INFO RTS token = d0e8e8e0-1b4d-4336-9999-000000000001\n12:12:14.652  INFO Deployment plan unit id for the call is 12345678-0c09-4510-a019-1234567890ab.\n12:12:14.684  INFO Assessing downloads for task App Calculator.\n12:12:14.685  INFO Assessing binary artifact calculator.apk.\n12:12:14.794  INFO Application com.tct.calculator is not present on POS. Install version 819013101\n12:12:14.812  INFO Binary file to download: 4649d16f-4aca-4d79-b96a-a085015fbf70->/packages/calculator.apk - 8107962 bytes\n12:12:14.813  INFO Volume assessment complete. Estimated volume to download: 7,73 Mb\n12:12:14.813  INFO Start downloads.\n12:12:14.813  INFO Download files for task App Calculator.\n12:12:21.606  INFO Files sent  - /packages/calculator.apk\n12:12:21.607  INFO Artifact calculator.apk downloaded successfully.\n12:12:21.607  INFO Task App Calculator completed.\n12:12:21.607  INFO Deployment unit completed.\n12:12:21.607  INFO Send service matrix.\n12:12:22.272  INFO Files sent  - /import/com.ingenico.dxmobile/matrixServices.json\n12:12:22.272  INFO Successful download of Available services request sent in file 'matrixServices.json' for target application 'com.ingenico.dxmobile'\n12:12:22.272  INFO Send RTS configuration.\n12:12:22.794  INFO Files sent  - /import/com.ingenico.dxmobile.rts/settings.rts.json\n12:12:22.795  INFO Successful download of RTS config file 'settings.rts.json' for target application 'com.ingenico.dxmobile.rts'\n12:12:22.795  INFO Maintenance call successfully completed.\n12:12:22.795  INFO Call ended successfully.\n12:12:22.795  INFO End of call - Duration: 10008ms\n12:12:22.797  INFO Deployment plan unit status is set to DOWNLOAD_DONE.\n";

// WILL BE USED
export function generateDeviceCallResourceReadV2Api(): DeviceCallResourceReadV2Api {
  return {
    device: {
      id: faker.string.uuid(),
      reference: faker.string.alphanumeric(12),
      serialNumber: faker.string.alphanumeric(12),
    },
    id: faker.string.uuid(),
    metadata: {
      callingApplicationPackageId: faker.internet.domainWord(),
      date: faker.date.past(),
      duration: faker.number.int({ min: 0, max: 3600 }),
      logEmitterType: 'DOWNLOAD',
      reason: faker.helpers.arrayElement(Object.values(enumCallReason)),
    },
    status: 'SUCCESS',
    component: {
      battery: {
        chargeLevel: `${faker.number.int({ min: 0, max: 100 })}%`,
        charging: 'CHARGING',
        cycleNumber: faker.number.int({ min: 0, max: 1000 }),
        partNumber: faker.string.alphanumeric(10),
        stateOfHealth: `${faker.number.int({ min: 70, max: 100 })}%`,
        temperature: `${faker.number.float({ min: 25, max: 60 }).toFixed(1)} °C`,
      },
      network: {
        bluetooth: {
          bondedDevices: [
            {
              deviceName: faker.word.words(2),
              status: 'CONNECTED',
            },
          ],
        },
        ethernet: {
          ipAddress: faker.internet.ip(),
          macAddress: faker.internet.mac(),
          status: 'CONNECTED',
        },
        ipAddress: faker.internet.ip(),
        mobile: {
          network: {
            name: faker.company.name(),
            type: faker.helpers.arrayElement(['4G', '5G', 'LTE', '3G']),
          },
          signalQuality: 'EXCELLENT',
          simOperator: faker.company.name(),
          simSlot: [
            {
              iccid: faker.string.numeric(19),
              imei: faker.string.numeric(15),
              imsi: faker.string.numeric(15),
              status: 'CONNECTED',
            },
          ],
        },
        wifi: {
          serviceSetId: faker.internet.domainWord(),
          status: 'CONNECTED',
        },
      },
    },
    logsAsString: LOGS_AS_STRING,
    statusDetail: faker.lorem.sentence(),
  };
}

// will be used
export function getDeviceCallByIdV2ResponseMock(): GetDeviceCallByIdV2QueryResponseApi {
  return generateDeviceCallResourceReadV2Api();
}

export function generateDeviceResourceSoftwareReadV2Api(): DeviceResourceSoftwareReadV2Api {
  return {
    applications: [
      {
        installDate: faker.date.past(),
        version: faker.system.semver(),
        packageName: faker.commerce.productName(),
        sizeOnDisc: faker.number.int({ min: 1_000_000, max: 500_000_000 }),
        versionCode: faker.system.semver(),
      },
    ],
  };
}

export function searchDiagnosticsV2CustomHandlerMock() {
  const count = faker.number.int({ min: 1, max: 9 });

  const deviceDiagnostics: DeviceResourceDiagnosticsListResponseApi = {
    pagination: generateInitialPaginationApi(count),
    data: faker.helpers.multiple((_) => generateDeviceDiagnosticsReadV2Api(), { count }),
  };

  return HttpResponse.json<DeviceResourceDiagnosticsListResponseApi>(deviceDiagnostics);
}

export function generateDeviceDiagnosticsReadV2Api(): DeviceResourceDiagnosticsReadApi {
  return {
    date: faker.date.past(),
    deviceId: faker.string.uuid(),
    id: faker.string.uuid(),
    serialNumber: faker.string.alphanumeric(9),
    type: enumDeviceDiagnosticsType.AUTOMATIC_DIAGNOSTICS,
    testResults: [
      {
        resultCode: 'TD3701',
        executionStatus: enumDiagnosticTestExecutionStatus.COMPLETED,
        module: enumDiagnosticTestModule.BATTERY,
        status: 'PASSED',
      },
      {
        resultCode: 'TD3701',
        executionStatus: enumDiagnosticTestExecutionStatus.COMPLETED,
        module: enumDiagnosticTestModule.CERTIFICATES,
        status: 'FAILED',
      },
      {
        resultCode: 'TD3701',
        executionStatus: enumDiagnosticTestExecutionStatus.ABORTED,
        module: enumDiagnosticTestModule.ETHERNET,
        status: 'WARNING',
      },
      {
        resultCode: 'TD3701',
        executionStatus: enumDiagnosticTestExecutionStatus.NOT_APPLICABLE,
        module: enumDiagnosticTestModule.MAG_STRIPE_READER,
        status: 'PASSED',
      },
      {
        resultCode: 'TD3701',
        executionStatus: enumDiagnosticTestExecutionStatus.COMPLETED,
        module: enumDiagnosticTestModule.ETHERNET,
        status: 'PASSED',
      },
    ],
  };
}

export function getDeviceDiagnosticDetailsByDeviceIdAndDiagnosticIdResponseMock(): GetDeviceDiagnosticByDeviceIdAndDiagnosticIdV2QueryResponseApi {
  return generateDeviceDiagnosticsReadV2Api();
}

export function getDeviceSoftwaresByDeviceIdV2CustomHandler() {
  return new HttpResponse(null, { status: 404 });
}

export function getDeviceSoftwaresByDeviceIdV2ResponseMock(): GetDeviceSoftwaresByDeviceIdV2QueryResponseApi {
  return generateDeviceResourceSoftwareReadV2Api();
}

export function generateDeviceResourceMetricsReadV2Api(): DeviceResourceMetricsReadV2Api {
  return {
    components: {
      battery: {
        chargeLevel: `${faker.number.int({ min: 0, max: 100 })}%`,
        charging: 'CHARGING',
        cycleNumber: faker.number.int({ min: 0, max: 1000 }),
        partNumber: faker.string.alphanumeric(12),
        stateOfHealth: `${faker.number.int({ min: 70, max: 100 })}%`,
        temperature: `${faker.number.float({ min: 20, max: 60 }).toFixed(1)} °C`,
      },
      cpu: {
        total: `${faker.number.int({ min: 1, max: 8 })} cores`,
        used: `${faker.number.float({ min: 0.1, max: 100 }).toFixed(2)}%`,
      },
      location: {
        accuracy: `${faker.number.float({ min: 1, max: 100 }).toFixed(1)} m`,
        latitude: faker.location.latitude().toString(),
        longitude: faker.location.longitude().toString(),
        provider: faker.company.name(),
        syncDate: faker.date.past(),
      },
      network: {
        bluetooth: {
          bondedDevices: [{ deviceName: faker.word.words(2), status: 'CONNECTED' }],
        },
        ethernet: {
          ipAddress: faker.internet.ip(),
          macAddress: faker.internet.mac(),
          status: 'CONNECTED',
        },
        ipAddress: faker.internet.ip(),
        mobile: {
          network: {
            name: faker.company.name(),
            type: faker.helpers.arrayElement(['4G', '5G', 'LTE']),
          },
          signalQuality: 'EXCELLENT',
          simOperator: faker.company.name(),
          simSlot: [
            {
              iccid: faker.string.numeric(19),
              imei: faker.string.numeric(15),
              imsi: faker.string.numeric(15),
              status: 'CONNECTED',
            },
          ],
        },
        wifi: {
          serviceSetId: faker.internet.domainWord(),
          status: 'CONNECTED',
        },
      },
      ram: {
        total: `${faker.number.int({ min: 2, max: 64 })} GB`,
        used: `${faker.number.int({ min: 0, max: 64 })} GB`,
      },
      storage: {
        total: `${faker.number.int({ min: 64, max: 1024 })} GB`,
        used: `${faker.number.int({ min: 0, max: 1024 })} GB`,
      },
    },
    refreshDate: faker.date.past(),
  };
}

export function getDeviceMetricsByDeviceIdV2ResponseMock(): GetDeviceMetricsByDeviceIdV2QueryResponseApi {
  return generateDeviceResourceMetricsReadV2Api();
}
export function generateDeviceReadV2Api(): DeviceResourceReadV2Api {
  return {
    id: faker.string.uuid(),
    isRtsEnabled: true,
    metadata: {
      device: {
        callScheduling: {
          callFrequency: 3,
          endTimeWindow: '23:55',
          nextCallDate: faker.date.future(),
          retryPeriod: 2,
          startTimeWindow: '00:05',
        },
        family: faker.commerce.productName(),
        manufacturerName: faker.company.name(),
        mode: 'MOCKUP',
        partNumber: faker.string.alphanumeric(10),
        reference: faker.string.alphanumeric(10),
        serialNumber: faker.string.alphanumeric(10),
      },
      system: {
        androidVersion: `Android ${faker.system.semver()}`,
        osName: faker.system.commonFileName(),
        osVersion: faker.system.semver(),
        pciVersion: faker.system.semver(),
        usdkVersion: faker.system.semver(),
      },
    },
    rts: {
      connectionStatus: enumDeviceConnectionStatusV2.NEVER_CONNECTED,
      lastConnectionDate: faker.date.past(),
      remoteDesktopStatus: enumRemoteDesktopStatusV2.UNAVAILABLE,
      rtsCapabilityStatus: 'RTS_NO_CAPABLE',
      deviceLogCapabilityStatus: enumDeviceLogCapabilityStatusV2.UNAVAILABLE,
    },
  };
}

export function getDeviceByIdV2ResponseMock(): GetDeviceByIdV2QueryResponseApi {
  return generateDeviceReadV2Api();
}
